import type { CellValue, SheetGrid } from './workbook.types';

type RichText = { richText: { text: string }[] };
type FormulaValue = { result?: unknown };
type HyperlinkValue = { text?: string };

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeCell = (value: unknown): CellValue => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (!isObject(value)) return null;

  if (Array.isArray((value as RichText).richText)) {
    return (value as RichText).richText.map(part => part.text).join('');
  }
  if ('result' in value) return normalizeCell((value as FormulaValue).result);
  if ('text' in value) return normalizeCell((value as HyperlinkValue).text);

  return null;
};

export const loadWorkbookGrids = async (file: File): Promise<SheetGrid[]> => {
  const { default: ExcelJS } = await import('exceljs');

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  return workbook.worksheets.map(worksheet => {
    const rows: CellValue[][] = [];

    worksheet.eachRow({ includeEmpty: true }, row => {
      // exceljs returns a 1-indexed sparse array whose slot 0 is never used.
      const values = row.values as unknown[];
      const cells: CellValue[] = [];
      for (let column = 1; column < values.length; column += 1) {
        cells.push(normalizeCell(values[column]));
      }
      rows.push(cells);
    });

    return { sheetName: worksheet.name, rows };
  });
};
